class TTDEmailPopup extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.storageKey = `ttd-email-popup:${this.dataset.sectionId}`;
    this.previouslyFocused = null;
    this.delayTimer = null;
    this.scrollHandler = null;

    this.querySelectorAll('[data-ttd-popup-close]').forEach((button) =>
      button.addEventListener('click', () => this.dismiss())
    );
    this.querySelector('[data-ttd-popup-copy]')?.addEventListener('click', () => this.copyCode());
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.dismiss();
    });
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dismiss();
    });
    this.dialog.addEventListener('close', () => this.restoreAfterClose());

    this.isConfirmedSuccessResponse = this.dataset.serverSuccess === 'true';
    this.isConfirmedErrorResponse = this.dataset.serverError === 'true';
    const shouldOpenForResponse = this.isConfirmedSuccessResponse || this.isConfirmedErrorResponse;

    if (window.Shopify?.designMode) {
      this.sectionSelectHandler = (event) => {
        if (event.target.contains(this)) this.open();
      };
      this.sectionDeselectHandler = (event) => {
        if (event.target.contains(this) && this.dialog.open) this.dialog.close();
      };
      document.addEventListener('shopify:section:select', this.sectionSelectHandler);
      document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
    }

    if (shouldOpenForResponse) {
      this.open();
      return;
    }

    if (this.dataset.auto !== 'true' || this.isSuppressed()) return;
    if (this.wasShownThisSession()) return;

    const trigger = this.dataset.trigger;
    if (trigger === 'delay' || trigger === 'either') {
      this.delayTimer = window.setTimeout(() => this.open(), Number(this.dataset.delay) * 1000);
    }
    if (trigger === 'scroll' || trigger === 'either') {
      this.scrollHandler = () => {
        const scrollable = document.documentElement.scrollHeight - window.innerHeight;
        const percent = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 0;
        if (percent >= Number(this.dataset.scroll)) this.open();
      };
      window.addEventListener('scroll', this.scrollHandler, { passive: true });
    }
  }

  disconnectedCallback() {
    window.clearTimeout(this.delayTimer);
    if (this.scrollHandler) window.removeEventListener('scroll', this.scrollHandler);
    if (this.sectionSelectHandler) document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
    if (this.sectionDeselectHandler) document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
  }

  open() {
    if (this.dialog.open) return;
    window.clearTimeout(this.delayTimer);
    if (this.scrollHandler) window.removeEventListener('scroll', this.scrollHandler);
    try {
      sessionStorage.setItem(`${this.storageKey}:shown`, 'true');
    } catch (_) {}
    this.previouslyFocused = document.activeElement;
    this.dialog.showModal();
    requestAnimationFrame(() => {
      const target = this.isConfirmedSuccessResponse
        ? this.querySelector('[data-ttd-popup-copy]')
        : this.querySelector('input:not([type="hidden"]), button');
      target?.focus();
    });
  }

  dismiss() {
    if (this.isConfirmedSuccessResponse) {
      this.persist('success', 365);
    } else {
      this.persist('dismissed', Number(this.dataset.dismissDays));
    }
    this.dialog.close();
  }

  restoreAfterClose() {
    if (this.previouslyFocused instanceof HTMLElement) this.previouslyFocused.focus();
  }

  persist(status, days) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify({ status, until: Date.now() + days * 86400000 }));
    } catch (_) {}
  }

  isSuppressed() {
    try {
      const state = JSON.parse(localStorage.getItem(this.storageKey));
      if (!state || state.until <= Date.now()) {
        localStorage.removeItem(this.storageKey);
        return false;
      }
      return state.status === 'dismissed' || state.status === 'success';
    } catch (_) {
      return false;
    }
  }

  wasShownThisSession() {
    try {
      return sessionStorage.getItem(`${this.storageKey}:shown`) === 'true';
    } catch (_) {
      return false;
    }
  }

  async copyCode() {
    const button = this.querySelector('[data-ttd-popup-copy]');
    const code = button?.dataset.code;
    if (!button || !code) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        const input = document.createElement('textarea');
        input.value = code;
        input.setAttribute('readonly', '');
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        input.remove();
      }
      if (this.isConfirmedSuccessResponse) this.persist('success', 365);
      button.textContent = button.dataset.copiedLabel;
      window.setTimeout(() => (button.textContent = button.dataset.copyLabel), 1800);
    } catch (_) {
      button.focus();
    }
  }
}

if (!customElements.get('ttd-email-popup')) customElements.define('ttd-email-popup', TTDEmailPopup);
